"use strict";
// packages/shared-react/src/hooks/useConversation.ts
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAgentApiConversationWithMessages = useAgentApiConversationWithMessages;
const recoil_1 = require("recoil");
const react_1 = require("react");
const shared_1 = require("@heartcheck/shared");
const AgentApiStore = __importStar(require("../state"));
const useConversations_1 = require("./useConversations");
const state_1 = require("../state");
/** ---------- Validation & helpers ---------- */
function isValidEvent(e) {
    try {
        return (typeof e?.id === "string" &&
            typeof e?.timestamp === "string" &&
            typeof e?.conversationId === "string" &&
            typeof e?.sender === "string" &&
            typeof e?.agentId === "string" &&
            !!(e?.event?.message ||
                e?.event?.innerDialog ||
                e?.event?.actionRequested ||
                e?.event?.actionResult));
    }
    catch {
        return false;
    }
}
function safeTimestamp(t) {
    if (!t)
        return 0;
    const n = Date.parse(t);
    return Number.isFinite(n) ? n : 0;
}
function extractGraphQLErrors(err) {
    if (!err)
        return [];
    const pools = [];
    if (Array.isArray(err?.errors))
        pools.push(err.errors);
    if (Array.isArray(err?.graphQLErrors))
        pools.push(err.graphQLErrors);
    if (Array.isArray(err?.response?.errors))
        pools.push(err.response.errors);
    if (Array.isArray(err?.data?.errors))
        pools.push(err.data.errors);
    const out = [];
    pools.forEach((arr) => (arr || []).forEach((e) => out.push({
        message: typeof e?.message === "string" ? e.message : undefined,
        errorType: typeof e?.errorType === "string" ? e.errorType : undefined,
    })));
    if (out.length === 0 && typeof err?.message === "string") {
        out.push({ message: err.message, errorType: undefined });
    }
    return out;
}
function isWelcomePending(err) {
    const errs = extractGraphQLErrors(err);
    for (let i = 0; i < errs.length; i++) {
        const m = (errs[i].message || "").toUpperCase();
        const t = (errs[i].errorType || "").toUpperCase();
        if (t === "WELCOME_PENDING" || m.includes("WELCOME_PENDING"))
            return true;
    }
    return false;
}
/** Build a fresh GraphQL query each time with a salt comment to defeat client/middleware caching */
function buildLoadEventsQuery(salt) {
    return new shared_1.GraphqlQuery(
    /* GraphQL */ `
      # poll-salt: ${salt}
      query EventsByConversation($conversationId: ID!) {
        eventsByConversationId(conversationId: $conversationId) {
          id
          conversationId
          sender
          timestamp
          agentId
          userId
          event {
            message
            innerDialog
            actionRequested
            actionResult
          }
        }
      }
    `, {
        // ✅ Use Cognito User Pools (ID token) → your /graphql proxy forwards JWT upstream
        forwardAuthMode: "userpools",
    });
}
/**
 * Normalize an entry from ConversationEvents so we always see a Loadable<ConversationEvent[]>
 */
function normalizeEventsEntry(entry) {
    if (!entry) {
        return state_1.Loadable.unloaded();
    }
    if (entry instanceof state_1.Loadable) {
        return entry;
    }
    if (Array.isArray(entry)) {
        return state_1.Loadable.loaded(entry);
    }
    return state_1.Loadable.unloaded();
}
/**
 * Safely derive the current Conversation from useAgentApiConversationList()
 * regardless of whether the hook is used in tuple or object style.
 */
function useCurrentConversation(id) {
    const conversations = (0, useConversations_1.useAgentApiConversationList)();
    const stateCandidate = conversations && conversations.state
        ? conversations.state
        : Array.isArray(conversations)
            ? conversations[0]
            : conversations;
    const state = stateCandidate && typeof stateCandidate.isUnloaded === "function"
        ? stateCandidate
        : state_1.Loadable.unloaded();
    if (state.isUnloaded() || state.loading === "loading" || !id) {
        return undefined;
    }
    const value = state.value;
    if (!value)
        return undefined;
    if (typeof value.get === "function") {
        // ObjRecord<Conversation> case
        return value.get(id);
    }
    if (typeof value.items === "function") {
        const arr = value.items();
        return arr.find((c) => c.id === id);
    }
    if (Array.isArray(value)) {
        return value.find((c) => c.id === id);
    }
    return undefined;
}
/** ---------- Hook ---------- */
function useAgentApiConversationWithMessages(id = "") {
    const conversation = useCurrentConversation(id);
    const [eventsState, setEventsState] = (0, recoil_1.useRecoilState)(AgentApiStore.ConversationEvents);
    const conversationEvents = normalizeEventsEntry(eventsState[id]);
    const stoppedRef = (0, react_1.useRef)(false);
    const inFlightRef = (0, react_1.useRef)(false);
    /** Merge logic */
    const mergeEvents = (0, react_1.useCallback)((incoming) => {
        if (!id || !incoming?.length)
            return;
        setEventsState((prev) => {
            const currentEntry = normalizeEventsEntry(prev[id]);
            const current = currentEntry.value ?? [];
            const byId = new Map();
            for (const e of current)
                if (e?.id)
                    byId.set(e.id, e);
            for (const e of incoming)
                if (e?.id)
                    byId.set(e.id, e);
            const merged = Array.from(byId.values())
                .filter(isValidEvent)
                .sort((a, b) => safeTimestamp(a.timestamp) - safeTimestamp(b.timestamp));
            return {
                ...prev,
                [id]: state_1.Loadable.loaded(merged),
            };
        });
    }, [id, setEventsState]);
    /** Poll once */
    const pollOnce = (0, react_1.useCallback)(async () => {
        if (!id || stoppedRef.current || inFlightRef.current)
            return;
        inFlightRef.current = true;
        const q = buildLoadEventsQuery(String(Date.now()));
        const anyQ = q;
        const run = anyQ.invoke ?? anyQ.execute ?? anyQ.run ?? anyQ.fetch;
        try {
            if (typeof run !== "function") {
                throw new Error("GraphqlQuery missing execute method (invoke/execute/run/fetch).");
            }
            const res = await run.call(q, { conversationId: id });
            const listRaw = res?.eventsByConversationId ??
                res?.data?.eventsByConversationId ??
                [];
            const cleaned = (Array.isArray(listRaw) ? listRaw : [])
                .filter(isValidEvent)
                .sort((a, b) => safeTimestamp(a.timestamp) - safeTimestamp(b.timestamp));
            mergeEvents(cleaned);
            if (typeof window !== "undefined" &&
                !stoppedRef.current &&
                cleaned.length < 2) {
                const delay = 220 + Math.floor(Math.random() * 180);
                window.setTimeout(() => {
                    if (!stoppedRef.current)
                        void pollOnce();
                }, delay);
            }
        }
        catch (err) {
            if (typeof window !== "undefined" &&
                isWelcomePending(err) &&
                !stoppedRef.current) {
                const delay = 220 + Math.floor(Math.random() * 180);
                window.setTimeout(() => {
                    if (!stoppedRef.current)
                        void pollOnce();
                }, delay);
            }
            else {
                // eslint-disable-next-line no-console
                console.error("❌ pollOnce failed:", err);
            }
        }
        finally {
            inFlightRef.current = false;
        }
    }, [id, mergeEvents]);
    /** Burst polling */
    const burstPoll = (0, react_1.useCallback)((times = 6, intervalMs = 300) => {
        if (!id || stoppedRef.current || typeof window === "undefined")
            return;
        for (let i = 0; i < times; i++) {
            window.setTimeout(() => {
                if (!stoppedRef.current)
                    void pollOnce();
            }, i * intervalMs);
        }
    }, [id, pollOnce]);
    /** Initial load */
    (0, react_1.useEffect)(() => {
        stoppedRef.current = false;
        inFlightRef.current = false;
        const shouldFetch = id &&
            (conversationEvents.loading === "unloaded" ||
                (conversationEvents.loading === "loaded" &&
                    (conversationEvents.value?.length ?? 0) === 0));
        if (!shouldFetch || typeof window === "undefined")
            return;
        let cancelled = false;
        setEventsState((prev) => ({
            ...prev,
            [id]: state_1.Loadable.loading(),
        }));
        const q = buildLoadEventsQuery(String(Date.now()));
        const anyQ = q;
        const run = anyQ.invoke ?? anyQ.execute ?? anyQ.run ?? anyQ.fetch;
        (async () => {
            try {
                if (typeof run !== "function") {
                    throw new Error("GraphqlQuery missing execute method (invoke/execute/run/fetch).");
                }
                inFlightRef.current = true;
                const res = await run.call(q, { conversationId: id });
                if (cancelled)
                    return;
                const raw = res?.eventsByConversationId ??
                    res?.data?.eventsByConversationId ??
                    [];
                const cleaned = (Array.isArray(raw) ? raw : [])
                    .filter(isValidEvent)
                    .sort((a, b) => safeTimestamp(a.timestamp) - safeTimestamp(b.timestamp));
                setEventsState((prev) => ({
                    ...prev,
                    [id]: state_1.Loadable.loaded(cleaned),
                }));
                if (cleaned.length < 2) {
                    burstPoll();
                }
            }
            catch (err) {
                if (cancelled)
                    return;
                if (isWelcomePending(err)) {
                    burstPoll(5, 250);
                }
                else {
                    // eslint-disable-next-line no-console
                    console.error("❌ Failed initial events fetch:", err);
                    setEventsState((prev) => ({
                        ...prev,
                        [id]: state_1.Loadable.loaded([]),
                    }));
                }
            }
            finally {
                inFlightRef.current = false;
            }
        })();
        return () => {
            cancelled = true;
            stoppedRef.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    /** Slow background poll */
    (0, react_1.useEffect)(() => {
        if (!id || typeof window === "undefined")
            return;
        void pollOnce();
        burstPoll();
        const slowInterval = window.setInterval(() => {
            void pollOnce();
        }, 7000);
        return () => {
            window.clearInterval(slowInterval);
            stoppedRef.current = true;
            inFlightRef.current = false;
        };
    }, [id, pollOnce, burstPoll]);
    const filteredEvents = (0, react_1.useMemo)(() => conversationEvents.value || [], [conversationEvents]);
    const loadingConversation = conversationEvents.loading !== "loaded";
    // ---------- HYBRID RETURN ----------
    // payload: the "real" data
    const payload = {
        loadingConversation,
        events: filteredEvents,
        conversation,
    };
    // legacy tuple-style: const [state] = useAgentApiConversationWithMessages(id)
    const hybrid = [payload];
    // attach object-style fields directly so new code can do:
    // const r = useAgentApiConversationWithMessages(id); r.events, r.conversation, etc.
    hybrid.loadingConversation = loadingConversation;
    hybrid.events = filteredEvents;
    hybrid.conversation = conversation;
    return hybrid;
}
